import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

/**
 * Turn a heading's text into a stable, URL-safe slug used as its DOM id so the
 * "On this page" rail can anchor-scroll to it.
 *
 * @param {React.ReactNode} children - The heading's rendered children.
 * @returns {string} A slug like "core-principles".
 */
const slugify = (children) => {
    const text = Array.isArray(children) ? children.join("") : String(children ?? "");
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
};

/**
 * Renders a document's raw Markdown into the Dev Guide's styled elements.
 *
 * Content comes from uploaded S3 files and is therefore untrusted, so it is
 * sanitized via rehype-sanitize and raw HTML is NOT enabled (no rehype-raw).
 * Element styling reuses the existing `dg-*` classes so fetched documents look
 * identical to the original hand-built layout.
 *
 * @param {object} props
 * @param {string} props.content - Raw Markdown text.
 */
const DevGuideMarkdown = ({ content }) => {
    const components = {
        // h2 carries an id so the right-rail TOC can scroll to it.
        h1: ({ node, children, ...props }) => (
            <h1 className="dg-h1" {...props}>{children}</h1>
        ),
        h2: ({ node, children, ...props }) => (
            <h2 id={slugify(children)} className="dg-h2" {...props}>{children}</h2>
        ),
        h3: ({ node, children, ...props }) => (
            <h3 id={slugify(children)} className="dg-h3" {...props}>{children}</h3>
        ),
        p: ({ node, ...props }) => <p className="dg-paragraph" {...props} />,
        ul: ({ node, ...props }) => <ul className="dg-bullets" {...props} />,
        ol: ({ node, ...props }) => <ol className="dg-bullets dg-bullets-ordered" {...props} />,
        li: ({ node, ...props }) => <li {...props} />,
        a: ({ node, ...props }) => (
            <a className="dg-md-link" target="_blank" rel="noopener noreferrer" {...props} />
        ),
        strong: ({ node, ...props }) => <strong className="dg-bullet-lead" {...props} />,
        blockquote: ({ node, ...props }) => <blockquote className="dg-md-quote" {...props} />,
        // Fenced code blocks (```), styled like the original terminal block.
        pre: ({ node, ...props }) => <pre className="dg-md-pre" {...props} />,
        code: ({ node, inline, className, children, ...props }) =>
            inline ? (
                <code className="dg-md-code-inline" {...props}>{children}</code>
            ) : (
                <code className={`dg-md-code ${className || ""}`} {...props}>{children}</code>
            ),
        table: ({ node, ...props }) => (
            <div className="dg-md-table-wrap">
                <table className="dg-md-table" {...props} />
            </div>
        ),
        th: ({ node, ...props }) => <th {...props} />,
        td: ({ node, ...props }) => <td {...props} />,
        hr: ({ node, ...props }) => <hr className="dg-divider" {...props} />,
    };

    return (
        <ReactMarkdown
            components={components}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
        >
            {content}
        </ReactMarkdown>
    );
};

export default DevGuideMarkdown;
